import React, {useContext, useEffect, useMemo, useRef, useState} from 'react';
import {Dimensions, Pressable, View} from 'react-native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import Icon from 'react-native-vector-icons/Ionicons';
import {useTranslation} from 'react-i18next';

import {FindifyDataContext} from '@/contexts/FindifyDataContext';
import {BasePage} from './helpers/BasePage';
import {DefaultShadow} from './helpers/DefaultShadows';
import {PlaceColors} from './helpers/PlaceColors';
import {FindifySimpleHeader} from './components/FindifySimpleHeader';
import {FindifyTopBarOverlay} from './components/FindifyTopBarOverlay';
import {PlaceText} from './components/PlaceText';
import {FindifyBasicModal} from './components/FindifyBasicModal';
import {MUSEUM_ART, MuseumArtItem} from './memoryGame/MuseumArt';

type Card = {
  id: string;
  art: MuseumArtItem;
};

const LEVELS: Array<{pairs: number; previewMs: number; solveMs: number}> = [
  {pairs: 3, previewMs: 3000, solveMs: 30000},
  {pairs: 4, previewMs: 3000, solveMs: 35000},
  {pairs: 6, previewMs: 3000, solveMs: 45000},
  {pairs: 8, previewMs: 3000, solveMs: 60000},
  {pairs: 10, previewMs: 3000, solveMs: 75000},
  {pairs: 12, previewMs: 3000, solveMs: 90000},
];

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value));

const shuffle = <T,>(arr: T[]) => {
  const copy = arr.slice();
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

const makeDeck = (level: number): Card[] => {
  const levelConfig = LEVELS[clamp(level, 0, LEVELS.length - 1)];
  const arts = shuffle(MUSEUM_ART).slice(0, levelConfig.pairs);
  const pairs: Card[] = arts.flatMap(art => [
    {id: `${art.key}-a`, art},
    {id: `${art.key}-b`, art},
  ]);
  return shuffle(pairs);
};

export const MemoryGamePage = ({navigation}: any) => {
  const findifyDataContext = useContext(FindifyDataContext);
  const insets = useSafeAreaInsets();
  const {t} = useTranslation();

  const [level, setLevel] = useState(0);
  const [deck, setDeck] = useState<Card[]>(() => makeDeck(0));
  const [flipped, setFlipped] = useState<number[]>([]);
  const [matched, setMatched] = useState<Set<string>>(new Set());
  const [moves, setMoves] = useState(0);

  const [isPreviewing, setIsPreviewing] = useState(true);
  const [previewLeftMs, setPreviewLeftMs] = useState(LEVELS[0].previewMs);
  const [showWin, setShowWin] = useState(false);
  const [showLose, setShowLose] = useState(false);
  const [solveLeftMs, setSolveLeftMs] = useState(LEVELS[0].solveMs);
  const [isSolving, setIsSolving] = useState(false);
  const lockRef = useRef(false);

  const totalCards = deck.length;
  const totalPairs = totalCards / 2;
  const progressPairs = matched.size;

  const grid = useMemo(() => {
    const screenWidth = Dimensions.get('window').width;
    const maxWidth = screenWidth * 0.92;
    const minTile = 62;
    const maxTile = 96;

    const estimatedCols = Math.ceil(Math.sqrt(totalCards));
    const cols = clamp(estimatedCols, 3, 6);
    const gap = 10;
    const tileSize = clamp(
      (maxWidth - gap * (cols - 1)) / cols,
      minTile,
      maxTile,
    );

    return {cols, gap, tileSize};
  }, [totalCards]);

  const resetLevel = (nextLevel: number) => {
    const safeLevel = clamp(nextLevel, 0, LEVELS.length - 1);
    const nextDeck = makeDeck(safeLevel);

    lockRef.current = false;
    setLevel(safeLevel);
    setDeck(nextDeck);
    setFlipped([]);
    setMatched(new Set());
    setMoves(0);
    setShowWin(false);
    setShowLose(false);
    setIsPreviewing(true);
    setPreviewLeftMs(LEVELS[safeLevel].previewMs);
    setSolveLeftMs(LEVELS[safeLevel].solveMs);
    setIsSolving(false);
  };

  useEffect(() => {
    resetLevel(0);
  }, []);

  useEffect(() => {
    if (!isPreviewing) {
      return;
    }

    const tickMs = 100;
    const interval = setInterval(() => {
      setPreviewLeftMs(ms => Math.max(0, ms - tickMs));
    }, tickMs);

    const timeout = setTimeout(() => {
      setIsPreviewing(false);
      setFlipped([]);
      setIsSolving(true);
    }, LEVELS[level].previewMs);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [isPreviewing, level]);

  useEffect(() => {
    if (!isSolving || showWin || showLose) {
      return;
    }

    const tickMs = 100;
    const interval = setInterval(() => {
      setSolveLeftMs(ms => Math.max(0, ms - tickMs));
    }, tickMs);

    return () => {
      clearInterval(interval);
    };
  }, [isSolving, showWin, showLose]);

  useEffect(() => {
    if (!isSolving) {
      return;
    }
    if (solveLeftMs > 0) {
      return;
    }

    lockRef.current = true;
    setIsSolving(false);
    setShowLose(true);
  }, [isSolving, solveLeftMs]);

  useEffect(() => {
    if (progressPairs === totalPairs && totalPairs > 0) {
      setIsSolving(false);
      setShowWin(true);
    }
  }, [progressPairs, totalPairs]);

  const isFaceUp = (index: number) => {
    if (isPreviewing) {
      return true;
    }
    if (flipped.includes(index)) {
      return true;
    }
    return matched.has(deck[index]?.art?.key ?? '');
  };

  const onPressCard = (index: number) => {
    if (isPreviewing) {
      return;
    }
    if (lockRef.current) {
      return;
    }
    if (matched.has(deck[index].art.key)) {
      return;
    }
    if (flipped.includes(index)) {
      return;
    }

    const nextFlipped = [...flipped, index];
    setFlipped(nextFlipped);

    if (nextFlipped.length === 2) {
      setMoves(m => m + 1);
      lockRef.current = true;

      const [a, b] = nextFlipped;
      const aKey = deck[a].art.key;
      const bKey = deck[b].art.key;

      if (aKey === bKey) {
        setMatched(prev => {
          const next = new Set(prev);
          next.add(aKey);
          return next;
        });
        setFlipped([]);
        lockRef.current = false;
      } else {
        setTimeout(() => {
          setFlipped([]);
          lockRef.current = false;
        }, 650);
      }
    }
  };

  const background = PlaceColors(findifyDataContext).background.get();
  const primary = PlaceColors(findifyDataContext).findifyPrimary.get();
  const text = PlaceColors(findifyDataContext).text.get();

  const levelConfig = LEVELS[level];
  const previewSeconds = Math.ceil(previewLeftMs / 1000);
  const solveSeconds = Math.ceil(solveLeftMs / 1000);

  return (
    <BasePage navigation={navigation}>
      <FindifyTopBarOverlay />
      <FindifySimpleHeader
        navigation={navigation}
        title={t('games.memory_museum.title', {defaultValue: 'Memory Museum'})}
      />

      <View
        style={{
          flex: 1,
          backgroundColor: background,
          paddingBottom: insets.bottom + 50,
          paddingHorizontal: 16,
        }}>
        <View
          style={{
            paddingTop: 16,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
          <View style={{gap: 4}}>
            <PlaceText style={{fontSize: 22, fontWeight: '800', color: text}}>
              {t('games.memory_museum.level_pairs', {
                defaultValue: 'Level {{level}} · {{pairs}} pairs',
                level,
                pairs: levelConfig.pairs,
              })}
            </PlaceText>
            <PlaceText
              style={{
                fontSize: 14,
                color: PlaceColors(findifyDataContext).text.withAlpha(180),
              }}>
              {t('games.memory_museum.status_line', {
                defaultValue:
                  'Time: {{seconds}}s · Moves: {{moves}} · Found: {{found}}/{{total}}',
                seconds: solveSeconds,
                moves,
                found: progressPairs,
                total: totalPairs,
              })}
            </PlaceText>
          </View>

          <View style={{flexDirection: 'row', gap: 10}}>
            <Pressable
              onPress={() => resetLevel(level)}
              style={[
                {
                  backgroundColor: primary,
                  paddingHorizontal: 14,
                  paddingVertical: 10,
                  borderRadius: 16,
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 8,
                },
                DefaultShadow,
              ]}
              hitSlop={{top: 10, bottom: 10, left: 10, right: 10}}>
              <Icon name="refresh" size={18} color="#FFF" />
              <PlaceText
                style={{color: '#FFF', fontSize: 14, fontWeight: '800'}}>
                {t('games.memory_museum.restart', {defaultValue: 'Restart'})}
              </PlaceText>
            </Pressable>
          </View>
        </View>

        <View style={{flex: 1, alignItems: 'center', justifyContent: 'center'}}>
          <View
            style={{
              width: grid.cols * grid.tileSize + (grid.cols - 1) * grid.gap,
            }}>
            <View
              style={{
                flexDirection: 'row',
                flexWrap: 'wrap',
                gap: grid.gap,
                justifyContent: 'center',
              }}>
              {deck.map((card, index) => {
                const faceUp = isFaceUp(index);
                const isDone = matched.has(card.art.key);
                const CardArt = card.art.Render;

                return (
                  <Pressable
                    key={card.id}
                    onPress={() => onPressCard(index)}
                    style={[
                      {
                        width: grid.tileSize,
                        height: grid.tileSize,
                        borderRadius: 18,
                        overflow: 'hidden',
                        backgroundColor: faceUp
                          ? '#FFFFFF'
                          : PlaceColors(findifyDataContext).primary.withAlpha(
                              160,
                            ),
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderWidth: 2,
                        borderColor: faceUp ? primary : 'transparent',
                        opacity: isDone ? 0.85 : 1,
                        transform: [{scale: isDone ? 0.98 : 1}],
                      },
                      DefaultShadow,
                    ]}>
                    {faceUp ? (
                      <View
                        style={{
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}>
                        <CardArt size={grid.tileSize * 0.8} />
                      </View>
                    ) : (
                      <View
                        style={{
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}>
                        <Icon name="sparkles" size={24} color="#FFF" />
                        <PlaceText
                          style={{
                            marginTop: 6,
                            color: '#FFF',
                            fontSize: 12,
                            fontWeight: '800',
                          }}>
                          {t('games.memory_museum.tap', {defaultValue: 'Tap!'})}
                        </PlaceText>
                      </View>
                    )}
                  </Pressable>
                );
              })}
            </View>
          </View>
        </View>

        {isPreviewing && (
          <View
            pointerEvents="none"
            style={{
              position: 'absolute',
              left: 16,
              right: 16,
              bottom: insets.bottom + 18,
              backgroundColor: '#00000066',
              borderRadius: 18,
              paddingHorizontal: 16,
              paddingVertical: 12,
            }}>
            <PlaceText style={{color: '#FFF', fontSize: 14, fontWeight: '800'}}>
              {t('games.memory_museum.preview_title', {
                defaultValue: 'Memorize! Cards hide in {{seconds}}s',
                seconds: previewSeconds,
              })}
            </PlaceText>
            <PlaceText style={{color: '#FFFFFFCC', fontSize: 12, marginTop: 2}}>
              {t('games.memory_museum.preview_subtitle', {
                defaultValue:
                  'More levels add more museum treasures in the same time.',
              })}
            </PlaceText>
          </View>
        )}

        <FindifyBasicModal
          isOpen={showWin}
          closeModal={() => setShowWin(false)}
          style={{padding: 16, width: '92%'}}>
          <View style={{gap: 12, width: '100%'}}>
            <PlaceText
              style={{
                color: text,
                fontSize: 22,
                fontWeight: '900',
                textAlign: 'center',
              }}>
              {t('games.memory_museum.win_title', {defaultValue: 'Great job!'})}
            </PlaceText>
            <PlaceText
              style={{
                color: text,
                fontSize: 16,
                fontWeight: '800',
                textAlign: 'center',
              }}>
              {t('games.memory_museum.win_body', {
                defaultValue:
                  'You found all {{total}} pairs in {{moves}} moves with {{seconds}}s left.',
                total: totalPairs,
                moves,
                seconds: solveSeconds,
              })}
            </PlaceText>

            <View style={{flexDirection: 'row', gap: 10, flexWrap: 'wrap'}}>
              <Pressable
                onPress={() => resetLevel(level)}
                style={[
                  {
                    backgroundColor: primary,
                    paddingHorizontal: 16,
                    paddingVertical: 12,
                    borderRadius: 16,
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 8,
                  },
                  DefaultShadow,
                ]}>
                <Icon name="play" size={18} color="#FFF" />
                <PlaceText
                  style={{color: '#FFF', fontSize: 14, fontWeight: '800'}}>
                  {t('games.memory_museum.play_again', {
                    defaultValue: 'Play again',
                  })}
                </PlaceText>
              </Pressable>

              {level < LEVELS.length - 1 && (
                <Pressable
                  onPress={() => resetLevel(level + 1)}
                  style={[
                    {
                      backgroundColor: '#FF006E',
                      paddingHorizontal: 16,
                      paddingVertical: 12,
                      borderRadius: 16,
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 8,
                    },
                    DefaultShadow,
                  ]}>
                  <Icon name="arrow-forward" size={18} color="#FFF" />
                  <PlaceText
                    style={{color: '#FFF', fontSize: 14, fontWeight: '800'}}>
                    {t('games.memory_museum.next_level', {
                      defaultValue: 'Next level',
                    })}
                  </PlaceText>
                </Pressable>
              )}
            </View>

            <View
              style={{
                flexDirection: 'row',
                gap: 8,
                flexWrap: 'wrap',
                marginTop: 6,
              }}>
              {LEVELS.map((l, idx) => (
                <Pressable
                  key={`level-${idx}`}
                  onPress={() => resetLevel(idx)}
                  style={{
                    paddingHorizontal: 10,
                    paddingVertical: 8,
                    borderRadius: 999,
                    borderWidth: 2,
                    borderColor:
                      idx === level
                        ? primary
                        : PlaceColors(findifyDataContext).text.withAlpha(40),
                    backgroundColor:
                      idx === level
                        ? PlaceColors(findifyDataContext).primary.withAlpha(60)
                        : 'transparent',
                  }}>
                  <PlaceText
                    style={{color: text, fontSize: 12, fontWeight: '800'}}>
                    L{idx} · {l.pairs}p
                  </PlaceText>
                </Pressable>
              ))}
            </View>
          </View>
        </FindifyBasicModal>

        <FindifyBasicModal
          isOpen={showLose}
          closeModal={() => setShowLose(false)}
          style={{padding: 16, width: '92%'}}>
          <View style={{gap: 12, width: '100%'}}>
            <PlaceText
              style={{
                color: '#FF006E',
                fontSize: 22,
                fontWeight: '900',
                textAlign: 'center',
              }}>
              {t('games.memory_museum.times_up_title', {
                defaultValue: "Time's up!",
              })}
            </PlaceText>
            <PlaceText
              style={{
                color: text,
                fontSize: 16,
                fontWeight: '800',
                textAlign: 'center',
              }}>
              {t('games.memory_museum.times_up_body', {
                defaultValue: "Try again — you've got this.",
              })}
            </PlaceText>

            <View style={{flexDirection: 'row', gap: 10, flexWrap: 'wrap'}}>
              <Pressable
                onPress={() => resetLevel(level)}
                style={[
                  {
                    backgroundColor: primary,
                    paddingHorizontal: 16,
                    paddingVertical: 12,
                    borderRadius: 16,
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 8,
                  },
                  DefaultShadow,
                ]}>
                <Icon name="refresh" size={18} color="#FFF" />
                <PlaceText
                  style={{color: '#FFF', fontSize: 14, fontWeight: '800'}}>
                  {t('games.memory_museum.retry_level', {
                    defaultValue: 'Retry level',
                  })}
                </PlaceText>
              </Pressable>

              <Pressable
                onPress={() => resetLevel(0)}
                style={[
                  {
                    backgroundColor: '#FF006E',
                    paddingHorizontal: 16,
                    paddingVertical: 12,
                    borderRadius: 16,
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 8,
                  },
                  DefaultShadow,
                ]}>
                <Icon name="home" size={18} color="#FFF" />
                <PlaceText
                  style={{color: '#FFF', fontSize: 14, fontWeight: '800'}}>
                  {t('games.memory_museum.back_to_level0', {
                    defaultValue: 'Back to L0',
                  })}
                </PlaceText>
              </Pressable>
            </View>
          </View>
        </FindifyBasicModal>
      </View>
    </BasePage>
  );
};
